/**
 * Holiday homework covers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_project_pages`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const holidayHomeworkCoversPack: ToilePack = {
    slug: 'holiday-homework-covers',
    category: 'classroom',
    name: 'Holiday homework covers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'summer', label: 'Summer', subject: 'a cover with a sun, ice cream and "HOLIDAY HOMEWORK"'},
        {key: 'winter', label: 'Winter', subject: 'a cover with snowflakes and a scarf'},
        {key: 'festival', label: 'Festival', subject: 'a cover with diyas and kites'},
    ],
    styles: [
        {key: 'sketch', label: 'Sketch pens', style: 'outlined in black fineliner and filled in with bright sketch pens in flat even colour, neat hand-drawn lines with a slight wobble', palette: 'sunny yellows, oranges, blues and pinks', framing: 'centred on a white A4 sheet seen straight on with an even clean margin all round'},
        {key: 'pencil', label: 'Colour pencils', style: 'drawn in sharp colour pencils with soft layered shading and visible pencil strokes, a gentle hand-coloured finish', palette: 'soft pencil tones of sky blue, peach and mint', framing: 'filling a white A4 sheet seen straight on, a narrow pencil-ruled margin line inside the edge of the page'},
        {key: 'duo', label: 'Two-colour pen', style: 'drawn in black gel pen with one accent colour of highlighter, clean ink lines, minimal and tidy', palette: 'black ink with orange highlighter only', framing: 'on a cream notebook page seen straight on with a generous margin'},
    ],
    directions: ['beach', 'monsoon', 'Diwali break'],
    directionHint: 'try: beach',
    seo: {
        title: 'Holiday homework covers: summer, winter, festival',
        metaDescription: 'Nine hand-drawn holiday homework covers: summer, winter and festival designs, in sketch pens, colour pencils or gel pen.',
        h1: 'Holiday homework covers: summer, winter, festival',
        tagline: 'Nine covers: summer, winter and festival, each in three pen styles.',
        intro: 'Front pages for holiday homework, drawn by hand with pens and colours. The summer cover has a sun, an ice cream and the words HOLIDAY HOMEWORK. The winter cover has snowflakes and a scarf. The festival cover has diyas and kites. Each comes in three pen styles. Type a twist, such as beach, monsoon or Diwali break, and the same three covers can take it.',
        useCases: [
            'a summer vacation homework file',
            'a winter break workbook',
            'a festival holiday project',
        ],
        faq: [
            {q: 'Which cover is for summer vacation?', a: 'The summer cover, with a sun, an ice cream and the words HOLIDAY HOMEWORK.'},
            {q: 'What is on the winter cover?', a: 'Snowflakes and a scarf.'},
            {q: 'Can I change the festival?', a: 'Yes. Type the festival you want, such as Diwali break, and it is a request for the same layout, not a promised result.'},
            {q: 'What is the difference between the three styles?', a: 'Sketch pens use bright flat colour inside black fineliner outlines. Colour pencils give soft layered shading in pastel tones. Two-colour pen is black gel ink with one highlighter colour.'},
        ],
    },
}
